import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronDown, ArrowRight, Sparkles, Store, Truck, Package, Leaf, Layers } from "lucide-react";
import { Brand, Eyebrow, StepNumber } from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const homeForRole = (role) => {
  if (role === "admin") return { path: "/admin", label: "Admin" };
  if (role === "city_official") return { path: "/city", label: "City Portal" };
  return { path: "/home", label: "Home" };
};

const TYPE_ICON = { retail: Store, delivery: Truck, distribution: Package, cultivation: Leaf, microbusiness: Layers };

const cfg = (token) => ({ withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });

export default function RoadmapPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const email = localStorage.getItem("email");
  const role = localStorage.getItem("role");
  const home = homeForRole(role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingType, setChangingType] = useState(false);
  const [openStages, setOpenStages] = useState({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}/roadmap`, cfg(token));
      setData(r.data);
      // expand the current + any incomplete stage; collapse completed ones
      setOpenStages((prev) => {
        if (Object.keys(prev).length) return prev;
        const o = {};
        (r.data.stages || []).forEach((s) => { o[s.id] = !s.complete; });
        return o;
      });
    } catch (e) {
      toast.error("Couldn't load your roadmap");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const prev = document.title; document.title = "Equal — Property Roadmap";
    load();
    return () => { document.title = prev; };
    // eslint-disable-next-line
  }, []);

  const chooseType = async (bt) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/roadmap/business-type`, { business_type: bt }, cfg(token));
      setData(r.data);
      setChangingType(false);
      setOpenStages((o) => ({ ...o, check: true }));
      toast.success("Roadmap tailored to your business");
    } catch (e) { toast.error("Couldn't save your choice"); }
    finally { setSaving(false); }
  };

  const toggleTask = async (task, next) => {
    if (task.auto_done) {
      toast.message("This step completes automatically", { description: "It's ticked from your activity in Equal." });
      return;
    }
    // optimistic
    setData((d) => d && applyLocalToggle(d, task.id, next));
    try {
      const r = await axios.post(`${API}/roadmap/task`, { task_id: task.id, done: next }, cfg(token));
      setData(r.data);
    } catch (e) { toast.error("Couldn't update that step"); load(); }
  };

  const openTool = (task) => {
    if (task.tool === "chat") {
      if (task.ai_question) localStorage.setItem("equal_chat_prefill", task.ai_question);
      navigate("/chat");
    } else if (task.tool === "studio") navigate("/studio");
    else if (task.tool === "comps") navigate("/comps");
  };

  const toolLabel = (task) => task.tool_label || (
    task.tool === "chat" ? (task.ai_question ? "Ask the AI advisor" : "Open AI advisor")
      : task.tool === "studio" ? "Open Design Studio"
        : task.tool === "comps" ? "Open Market Data" : null);

  const pct = useMemo(() => {
    if (!data) return 0;
    const { done, total } = data.progress;
    return total ? Math.round((done / total) * 100) : 0;
  }, [data]);

  return (
    <div className="min-h-screen flex flex-col bg-page" data-testid="roadmap-page">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-4 px-5 md:px-7 h-14 bg-white border-b border-line">
        <Brand section="Property Roadmap" hint="Real estate, step by step" />
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={() => navigate("/studio")} className="btn-ghost btn-sm" data-testid="roadmap-studio-link">Design Studio</button>
          <button onClick={() => navigate("/comps")} className="btn-ghost btn-sm" data-testid="roadmap-comps-link">Market Data</button>
          <button onClick={() => navigate(home.path)} className="btn-ghost btn-sm" data-testid="roadmap-back-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back to {home.label}
          </button>
          <div className="w-px h-4 bg-line" />
          <span className="hidden md:inline text-mute text-xs font-mono">{email}</span>
          <button onClick={() => { localStorage.clear(); navigate("/"); }} className="btn-quiet btn-sm" data-testid="roadmap-logout-button">Sign out</button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-5 md:px-6 py-8 md:py-10">
        {loading ? (
          <div className="flex items-center gap-3 text-mute text-sm py-20 justify-center">
            <div className="w-5 h-5 border-2 border-line border-t-equal rounded-full animate-spin" /> Loading your roadmap…
          </div>
        ) : !data ? null : (!data.business_type || changingType) ? (
          <TypePicker options={data.business_type_options} current={data.business_type} saving={saving}
            onPick={chooseType} onCancel={data.business_type ? () => setChangingType(false) : null} />
        ) : (
          <>
            {/* Intro + progress */}
            <div className="animate-rise-in">
              <Eyebrow className="text-equal-dark">Your property roadmap</Eyebrow>
              <h1 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-ink leading-tight">
                From what you need to a signed lease.
              </h1>
              <p className="mt-3 text-mute text-base leading-relaxed">
                One clear step at a time. Equal links each step to the tools that help, and a few tick themselves off as you work.
              </p>

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button onClick={() => setChangingType(true)} data-testid="roadmap-change-type-btn"
                  className="pill hover:border-[#b9c4d0] transition-colors">
                  {data.business_type_label} · change
                </button>
                <span className="font-mono text-xs text-mute" data-testid="roadmap-progress">
                  {data.progress.done} of {data.progress.total} steps
                </span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-line overflow-hidden" data-testid="roadmap-progress-bar">
                <div className="h-full bg-equal rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>

            {/* Next step / finish */}
            {data.finished ? (
              <div className="mt-8 card-flat p-7 bg-mint border-[#bfe6cf]" data-testid="roadmap-finished">
                <Eyebrow className="text-equal-dark">Finish line</Eyebrow>
                <h2 className="mt-2 text-xl font-bold text-ink tracking-tight">Space secured — your roadmap is complete.</h2>
                <p className="mt-2 text-mute text-sm leading-relaxed">
                  You've worked all the way from defining your needs to signing a lease. Keep the Design Studio plan and Market Data handy as you move toward permitting.
                </p>
              </div>
            ) : data.next_step && (
              <div className="mt-8 card-flat p-6 md:p-7 ring-1 ring-equal/15" data-testid="roadmap-next-step">
                <div className="flex items-center gap-2 text-equal-dark">
                  <Sparkles className="w-4 h-4" />
                  <Eyebrow className="text-equal-dark">Your next step</Eyebrow>
                </div>
                <h2 className="mt-2 text-xl font-bold text-ink tracking-tight" data-testid="roadmap-next-step-title">
                  {data.next_step.task.title}
                </h2>
                <p className="mt-2 text-ink-2 text-sm leading-relaxed">{data.next_step.task.why}</p>
                {data.next_step.task.kb_hint && (
                  <p className="mt-2 text-mute text-xs italic">{data.next_step.task.kb_hint}</p>
                )}
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  {data.next_step.task.tool && (
                    <button onClick={() => openTool(data.next_step.task)} className="btn-primary btn-sm" data-testid="roadmap-next-action-btn">
                      {toolLabel(data.next_step.task)} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {!data.next_step.task.auto && (
                    <button onClick={() => toggleTask(data.next_step.task, true)} className="btn-ghost btn-sm" data-testid="roadmap-next-done-btn">
                      <Check className="w-3.5 h-3.5" /> Mark done
                    </button>
                  )}
                  {data.next_step.task.auto && (
                    <span className="text-mute text-xs">Completes automatically when you do it in Equal.</span>
                  )}
                </div>
              </div>
            )}

            {/* Stages */}
            <div className="mt-10 space-y-3">
              {data.stages.map((stage, i) => {
                const isOpen = !!openStages[stage.id];
                const state = stage.complete ? "done" : (data.progress.current_stage_id === stage.id ? "current" : "upcoming");
                return (
                  <div key={stage.id} className="card-flat overflow-hidden" data-testid={`roadmap-stage-${stage.id}`}>
                    <button onClick={() => setOpenStages((o) => ({ ...o, [stage.id]: !o[stage.id] }))}
                      className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-page transition-colors"
                      data-testid={`roadmap-stage-toggle-${stage.id}`}>
                      {state === "done"
                        ? <span className="w-[22px] h-[22px] rounded-full bg-equal grid place-items-center flex-none"><Check className="w-3.5 h-3.5 text-white" /></span>
                        : <StepNumber n={i + 1} active={state === "current"} />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-ink text-[15px] tracking-tight truncate">{stage.title}</span>
                          {state === "current" && <span className="pill bg-mint border-[#bfe6cf] text-equal-dark">Now</span>}
                        </div>
                        <p className="text-mute text-xs mt-0.5 truncate">{stage.subtitle}</p>
                      </div>
                      <span className="font-mono text-[11px] text-mute flex-none">{stage.done}/{stage.total}</span>
                      <ChevronDown className={`w-4 h-4 text-mute flex-none transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>

                    {isOpen && (
                      <div className="border-t border-line divide-y divide-line/60">
                        {stage.tasks.map((task) => (
                          <div key={task.id} className="px-5 py-4 flex gap-3" data-testid={`roadmap-task-${task.id}`}>
                            <button onClick={() => toggleTask(task, !task.done)}
                              disabled={task.auto_done}
                              className={`mt-0.5 w-[20px] h-[20px] rounded-[6px] border flex-none grid place-items-center transition-colors ${task.done ? "bg-equal border-equal" : "bg-white border-[#c9d1d9] hover:border-equal"} ${task.auto_done ? "cursor-default" : "cursor-pointer"}`}
                              title={task.auto_done ? "Completes automatically from your activity" : (task.done ? "Mark not done" : "Mark done")}
                              data-testid={`roadmap-task-checkbox-${task.id}`}>
                              {task.done && <Check className="w-3 h-3 text-white" />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[15px] font-semibold ${task.done ? "text-mute line-through" : "text-ink"}`}>{task.title}</span>
                                {task.auto && <span className="pill" title="Ticks itself off from your activity in Equal">Auto</span>}
                                {task.finish && <span className="pill bg-mint border-[#bfe6cf] text-equal-dark">Finish line</span>}
                              </div>
                              <p className="text-ink-2 text-sm mt-1 leading-relaxed">{task.why}</p>
                              {task.kb_hint && <p className="text-mute text-xs mt-1 italic">{task.kb_hint}</p>}
                              {task.tool && (
                                <button onClick={() => openTool(task)} className="btn-ghost btn-sm mt-3" data-testid={`roadmap-task-action-${task.id}`}>
                                  {toolLabel(task)} <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="mt-8 text-center text-mute text-xs leading-relaxed">
              This roadmap covers finding and securing a compliant space. Licensing, permitting and build-out come after your lease is signed.
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function applyLocalToggle(data, taskId, next) {
  const stages = data.stages.map((s) => {
    let done = s.done;
    const tasks = s.tasks.map((t) => {
      if (t.id !== taskId) return t;
      if (t.done !== next) done += next ? 1 : -1;
      return { ...t, done: next };
    });
    return { ...s, tasks, done, complete: s.total > 0 && done === s.total };
  });
  const progress = { ...data.progress, done: Math.max(0, data.progress.done + (next ? 1 : -1)) };
  return { ...data, stages, progress };
}

function TypePicker({ options, current, saving, onPick, onCancel }) {
  return (
    <div className="animate-rise-in" data-testid="roadmap-type-picker">
      <Eyebrow className="text-equal-dark">One quick question</Eyebrow>
      <h1 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-ink leading-tight">
        What are you building?
      </h1>
      <p className="mt-3 text-mute text-base leading-relaxed">
        We'll tailor your roadmap to your business type. You can change this anytime.
      </p>
      <div className="mt-7 grid sm:grid-cols-2 gap-3">
        {options.map((o) => {
          const Icon = TYPE_ICON[o.id] || Layers;
          const active = current === o.id;
          return (
            <button key={o.id} onClick={() => onPick(o.id)} disabled={saving}
              className={`card-flat p-5 text-left flex items-center gap-4 transition-all hover:border-equal hover:shadow-sm disabled:opacity-60 ${active ? "border-equal ring-1 ring-equal/20" : ""}`}
              data-testid={`roadmap-type-option-${o.id}`}>
              <span className="w-10 h-10 rounded-[10px] bg-mint grid place-items-center flex-none">
                <Icon className="w-5 h-5 text-equal-dark" />
              </span>
              <span className="font-bold text-ink text-[15px]">{o.label}</span>
              {active && <Check className="w-4 h-4 text-equal ml-auto" />}
            </button>
          );
        })}
      </div>
      {onCancel && (
        <button onClick={onCancel} className="btn-quiet btn-sm mt-5" data-testid="roadmap-type-cancel">Cancel</button>
      )}
    </div>
  );
}
